import { ChangeDetectionStrategy, DestroyRef, inject, Component, Inject, OnChanges, OnInit, output, SimpleChanges, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoggerService } from '@/core/services/logger.service';

import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ToolbarModule } from 'primeng/toolbar';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { ConsultorioCompartilhamentoRequest } from '@/core/api/sicom-api/registro/medico/consultorio-privado/consultorio-compartilhamento.model';
import { ConsultorioCompartilhamentoFeatureService } from './consultorio-compartilhamento.service';
import { finalize } from 'rxjs/operators';
import { ActionChildComponent } from '../../../../../../../shared/components/containers/action-child.component';
import { BotaoAcoesType } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { MenuItem } from 'primeng/api';
import { Pesquisa } from '@/shared/components/pessoa/pesquisa/pesquisa';
import { PessoaDomain } from '@/core/api/sicom-api/registro/pessoa/pessoa.domain';
import { CustomTableComponent, TableMetadata, TablePerfilValue } from '@/shared/components/layout/custom-table/custom-table.component';
import { FotoMedicoCacheService } from '@/shared/components/foto-diretor/foto-medico-cache.service';

import { CustomClass } from '@/shared/classes/custom.class';
import { CardContent } from '@/shared/components/card-content/card-content';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

interface CompartilhamentoRow {
    id: string;
    idSolicitante: number;
    uf: string;
    crm: string;
    nome: TablePerfilValue;
    entrada?: string;
    saida?: string;
}

@Component({
    selector: 'app-consultorio-compartilhamento',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonModule, TooltipModule, ToolbarModule, CardModule, InputTextModule, DatePickerModule, ReactiveFormsModule, Pesquisa, CustomTableComponent, CardContent],
    templateUrl: './consultorio-compartilhamento.html',
    styleUrl: './consultorio-compartilhamento.scss'
})
/** Aba de compartilhamento do consultório privado. */
export class ConsultorioCompartilhamentoComponent extends ActionChildComponent implements OnChanges, OnInit {
    private readonly logger = inject(LoggerService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly fotos = inject(FotoMedicoCacheService);
    constructor(
        @Inject(ConsultorioCompartilhamentoFeatureService)
        private feature: ConsultorioCompartilhamentoFeatureService
    ) {
        super();
    }

    protected readonly roleConsultorioEditar = PERMISSOES.medicos.consultorio.editar;

    tableMetadata: TableMetadata = {
        headers: {
            uf: { type: 'text', value: 'UF', hidden: false, default: '' },
            nome: { type: 'perfil', value: 'Nome', hidden: false, default: '' },
            entrada: { type: 'text', value: 'Data de Entrada', hidden: false, default: '' },
            saida: { type: 'text', value: 'Data de Saída', hidden: false, default: '' }
        },
        title: 'nome',
        actions: {
            remover: {
                label: '',
                icon: 'pi pi-trash',
                color: 'danger',
                permission: this.roleConsultorioEditar,
                command: (item: unknown) => this.deletar(item)
            }
        }
    };

    actions: BotaoAcoesType & { useEdit: boolean } = {
        items: [
            {
                label: 'Adicionar',
                icon: 'pi pi-plus-circle',
                command: () => this.novo(),
                styleClass: 'bg-green-500',
                permission: this.roleConsultorioEditar
            }
        ],
        useEdit: false
    };

    // Pessoa de contexto
    pessoa = input<Pessoa>();
    // Consultório selecionado
    idConsultorio = input<number | null>(null);
    // Quando true, abre o formulário em modo edição automaticamente (uso no wizard de novo consultório)
    autoEdit = input<boolean>(false);

    // Dados da grid
    rows = signal<CompartilhamentoRow[]>([]);

    // Emite mudanças da lista de médicos adicionados
    rowsChange = output<CompartilhamentoRow[]>();

    // Estados
    loading = signal(false);
    erro = signal<string | undefined>(undefined);

    // Estados de ação (placeholders visuais)
    isEditing = false;
    hasSelection = false;

    // Formulário de cadastro
    private readonly fb = inject(FormBuilder);
    readonly form = this.fb.group({
        nome: [{ value: '', disabled: true }, [Validators.required]],
        idSolicitante: this.fb.control<number | null>(null, [Validators.required]),
        uf: [''],
        dtEntrada: this.fb.control<Date | null>(null, [Validators.required]),
        dtSaida: this.fb.control<Date | null>(null)
    });

    // Diálogo de pesquisa de pessoa/médico
    showPesquisa = false;
    selectedRow: CompartilhamentoRow | null = null;

    override initComponent(): void {}

    override ngOnInit(): void {
        super.ngOnInit();
        if (this.autoEdit()) {
            this.isEditing = true;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['pessoa'] || changes['idConsultorio']) {
            const p = this.pessoa();
            const idC = this.idConsultorio();
            if (p && p.id !== undefined && p.id !== null && !!p.uf && typeof idC === 'number') {
                this.carregar({ id: p.id, uf: p.uf, idConsultorio: idC });
            } else {
                this.rows.set([]);
            }
        }
    }

    private carregar(params: Pessoa & { idConsultorio: number }): void {
        this.loading.set(true);
        this.erro.set(undefined);
        this.feature
            .buscar(params)
            .pipe(finalize(() => this.loading.set(false)))
            .subscribe({
                next: (lista: ConsultorioCompartilhamentoRequest[] | null | undefined) => {
                    const itens = lista ?? [];
                    this.rows.set(
                        itens.map((r: ConsultorioCompartilhamentoRequest, idx: number) => ({
                            id: `${r.sgUf ?? ''}-${r.nmSolicitante ?? ''}-${r.dtInicio ?? ''}-${r.dtFim ?? ''}-${idx}`,
                            idSolicitante: Number(r.idSolicitante ?? 0),
                            uf: r.sgUf ?? '',
                            crm: r.nuCrm ?? '',
                            nome: this.fotos.perfilMedico(r.nmSolicitante, r.nuCrm, r.sgUf),
                            entrada: this.formatarData(r.dtInicio),
                            saida: this.formatarData(r.dtFim)
                        }))
                    );
                    // `rows` guarda linhas já montadas, não um `computed` sobre o cache: o retrato
                    // que chega depois precisa reescrevê-las, senão só apareceria na próxima carga.
                    this.fotos
                        .precarregar(itens.map((r) => ({ crm: r.nuCrm, uf: r.sgUf })))
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe(() => this.rows.update((linhas) => linhas.map((linha) => ({ ...linha, nome: this.fotos.perfilMedico(linha.nome.nome, linha.crm, linha.uf) }))));
                    this.rowsChange.emit(this.rows());
                },
                error: (err: unknown) => {
                    this.logger.error('Erro ao buscar compartilhamento do consultório', err);
                    this.rows.set([]);
                    this.erro.set('Falha ao carregar compartilhamentos do consultório.');
                }
            });
    }

    private formatarData(valor?: string | null | Date): string {
        if (!valor) return '';
        if (valor instanceof Date) {
            if (isNaN(valor.getTime())) return '';
            const dd = String(valor.getDate()).padStart(2, '0');
            const mm = String(valor.getMonth() + 1).padStart(2, '0');
            const yyyy = valor.getFullYear();
            return `${dd}/${mm}/${yyyy}`;
        }
        const m = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) {
            const [, yyyy, mm, dd] = m;
            return `${dd}/${mm}/${yyyy}`;
        }
        try {
            const d = new Date(valor);
            if (isNaN(d.getTime())) return '';
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}/${mm}/${yyyy}`;
        } catch {
            return '';
        }
    }

    // Ações de UI
    novo() {
        this.isEditing = true;
        this.resetFormNovaInclusao();
    }
    editar() {
        if (!this.hasSelection) return;
        this.isEditing = true;
    }
    salvar() {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const { uf, dtEntrada, dtSaida, idSolicitante } = this.form.getRawValue();
        const nome = this.form.get('nome')?.value ?? '';
        const novo: CompartilhamentoRow = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            idSolicitante: Number(idSolicitante ?? 0),
            uf: uf || '',
            // A pesquisa de pessoa não devolve CRM, então a linha recém-incluída fica nas iniciais:
            // sem CRM não há chave de retrato. A foto aparece na próxima carga, vinda da API.
            crm: '',
            nome: this.fotos.perfilMedico(nome, null, uf),
            entrada: this.formatarData(dtEntrada),
            saida: this.formatarData(dtSaida)
        };
        // Como ainda não existe API de inserção, atualizamos a grid localmente (empilha mantendo os já adicionados)
        this.rows.set([novo, ...this.rows()]);
        // Mantém o formulário aberto para permitir múltiplas inclusões em sequência
        this.resetFormNovaInclusao();
        this.isEditing = true;
        this.rowsChange.emit(this.rows());
    }
    deletar(item?: unknown) {
        const row = item || this.selectedRow;
        if (!row) return;
        this.rows.set(this.rows().filter((r) => r !== row));
        if (row === this.selectedRow) {
            this.selectedRow = null;
            this.hasSelection = false;
        }
        this.rowsChange.emit(this.rows());
    }
    cancelar() {
        this.isEditing = false;
    }

    abrirPesquisa() {
        this.showPesquisa = true;
    }
    onPessoaSelecionada(p: PessoaDomain) {
        if (!p) return;
        // Preenche form com médico selecionado
        this.form.patchValue({
            nome: p.nome ?? '',
            idSolicitante: p.id ?? null,
            uf: p.uf ?? ''
        });
        this.form.get('nome')?.disable({ emitEvent: false });
        this.showPesquisa = false;
    }

    private resetFormNovaInclusao(): void {
        this.form.reset({ nome: { value: '', disabled: true }, idSolicitante: null, uf: '', dtEntrada: null, dtSaida: null });
        // Garante que o campo nome fica desabilitado após reset
        this.form.get('nome')?.disable({ emitEvent: false });
    }

    override getDefinedActions(): BotaoAcoesType & { useEdit: boolean } {
        return this.actions;
    }

    override updateActionsWithIndexes(actions: [MenuItem, number][]) {}
    override toggleAction(action: MenuItem): MenuItem {
        return action;
    }

    campoInvalido(nomeCampo: string): boolean {
        const campo = this.form?.get(nomeCampo);
        return !!campo && campo.invalid && (campo.touched || campo.dirty);
    }

    mensagemErroCampo(nomeCampo: string): string | null {
        const campo = this.form?.get(nomeCampo);
        if (!campo?.errors) return null;
        if (campo.errors['required']) return 'Campo obrigatório.';
        return 'Valor inválido.';
    }
}
